<template>
  <div class="contact-wizard">
    <h4>{{ labels.get("Get in contact") }}</h4>
    <div class="hr"></div>
    <div class="step-container">
      <ContactTypeSelector
        ref="step1"
        :class="{ visible: currentStep === config.steps.contactTypeSelector }"
      ></ContactTypeSelector>
      <ProductContactSelector
        ref="step2"
        :class="{
          visible: currentStep === config.steps.productContactSelector,
        }"
      ></ProductContactSelector>
      <ContactList
        ref="step3"
        :tag="config.mediaTagId"
        :class="{ visible: currentStep === config.steps.contactList }"
      ></ContactList>
      <ContactForm
        ref="step6"
        :class="{ visible: currentStep === 6 }"
        @rewind="onRewind"
      ></ContactForm>
      <SubmitForm
        ref="submitStep"
        :class="{ visible: currentStep === config.steps.submitForm }"
        @rewind="onRewind"
        @back="reviseData()"
        allow-restart
      ></SubmitForm>
    </div>
    <div class="button-container">
      <FlatButton
        secondary
        :label="labels.get('Back')"
        v-if="history.length > 1 && this.currentStep < 1000"
        class="white"
        @click="onBack"
      ></FlatButton>
      <div class="spacer"></div>
      <FlatButton
        primary
        blue
        :label="labels.get('Next')"
        v-if="!isLastStep && this.currentStep < 1000"
        :disabled="!canAdvance"
        @click="onNext"
      ></FlatButton>
      <FlatButton
        primary
        blue
        :label="labels.get('Submit')"
        v-if="isLastStep && this.currentStep < 1000"
        @click="onSubmit"
      ></FlatButton>
    </div>
  </div>
</template>

<script>
import { FlatButton } from "clariant-vue3-forms";
import ServiceManager from "../services/service-manager";
import ContactTypeSelector from "./ContactTypeSelector.vue";
import ProductContactSelector from "./ProductContactSelector.vue";
import ContactForm from "./ContactForm.vue";
import ContactList from "./ContactList.vue";
import SubmitForm from "./SubmitForm.vue";

export default {
  name: "Wizard",
  components: {
    FlatButton,
    ContactTypeSelector,
    ProductContactSelector,
    ContactForm,
    ContactList,
    SubmitForm,
  },
  data() {
    return {
      labels: ServiceManager.getService(this, "Labels"),
      config: ServiceManager.getService(this, "Config"),
      state: ServiceManager.getService(this, "State"),
      utils: ServiceManager.getService(this, "Utils"),
      currentStep: 1,
      history: [1],
      canAdvance: false,
      isLastStep: false,
    };
  },
  setup() {
    return {
      uniqueId: `Wizard-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`
    }
  },
  mounted() {
    this.state.subscribe(
      this.uniqueId,
      this.onStateChanged
    );
    this.setInitialPage();
  },
  beforeUnmount() {
    this.state.unsubscribe(this.uniqueId);
  },
  methods: {
    setInitialPage() {
      if (this.config.contactId) {
        this.currentStep = this.config.steps.contactForm;
        this.history = [this.config.steps.contactForm];
        let updatedState = {
          bu: "",
          bl: "",
          currentContact: this.config.contactId,
          currentProduct: "",
        };
        this.state.atomicUpdate("reload", updatedState); //force reload contact
        this.$nextTick(() => {
          this.updateButtons();
        });
      } else if (this.utils.isNewsPage()) {
        this.currentStep = this.config.steps.contactList;
        this.history.push(this.config.steps.contactList);
        this.$nextTick(() => {
          this.updateButtons();
        });
      } else if (this.utils.isCareersPage()) {
        this.currentStep = this.config.steps.contactForm;
        this.history = [this.config.steps.contactForm];
        let updatedState = {
          bu: this.config.careersTagId,
          bl: "",
          currentContact: "",
          currentProduct: "",
        };
        this.state.atomicUpdate("reload", updatedState); //force reload contact
        this.$nextTick(() => {
          this.updateButtons();
        });
      } else if (this.utils.isInvestorsPage()) {
        this.currentStep = this.config.steps.contactForm;
        this.history = [this.config.steps.contactForm];
        let updatedState = {
          bu: this.config.investorRelationsTagId,
          bl: "",
          currentContact: "",
          currentProduct: "",
        };
        this.state.atomicUpdate("reload", updatedState); //force reload contact
        this.$nextTick(() => {
          this.updateButtons();
        });
      } else if (this.state.current.currentProduct) {
        this.currentStep = this.config.steps.contactForm;
        this.history = [this.config.steps.contactForm];
        let updatedState = {
          bu: "",
          bl: "",
          currentContact: "",
          currentProduct: this.state.current.currentPage,
        };
        this.state.atomicUpdate("reload", updatedState); //force reload contact
        this.$nextTick(() => {
          this.updateButtons();
        });
      } else if (this.state.get("bu")) {
        this.currentStep = this.config.steps.contactForm;
        this.history = [this.config.steps.contactForm];
        this.state.atomicUpdate("reload", {}); //force reload contact
        this.$nextTick(() => {
          this.updateButtons();
        });
      }
    },
    onBack() {
      if (this.history.length < 2) return; //there's nowhere to go back to
      this.history.pop();
      this.currentStep = this.history[this.history.length - 1];
      this.$nextTick(() => {
        this.updateButtons();
      });
    },
    onNext() {
      const stepRef = this.$refs[`step${this.currentStep}`];
      if (!stepRef) return;
      if (!stepRef.canAdvance || !stepRef.canAdvance()) return;
      let nextStep = stepRef.getNextStep();
      if (nextStep === this.config.steps.locations) {
        window.open(this.config.locationsPath, "_self");
      } else if (nextStep === this.config.steps.sampleOrder) {
        window.open(this.state.current.currentSampleOrderUrl, "_self");
      } else if (nextStep === this.config.steps.productSearch) {
        window.open(this.config.productSearchPath, "_self");
      } else if (nextStep === this.config.steps.procurement) {
        window.open(this.config.procurementPath, "_self");
      } else if (nextStep) {
        this.currentStep = nextStep;
        this.history.push(nextStep);
      }
      this.$nextTick(() => {
        this.updateButtons();
      });
    },
    updateButtons() {
      const stepRef = this.$refs[`step${this.currentStep}`];
      if (!stepRef) return;
      this.canAdvance = (stepRef.canAdvance && stepRef.canAdvance()) || false;
      this.isLastStep = this.currentStep === this.config.steps.contactForm;
    },
    onStateChanged(message, prop, value) {
      if (prop === "canAdvance") {
        this.canAdvance = value;
      }
    },
    onRewind() {
      this.history = [this.config.steps.contactTypeSelector];
      this.currentStep = this.config.steps.contactTypeSelector;
      this.state.clear();
      this.updateButtons();
    },
    onSubmit() {
      if (!this.isLastStep) return;
      const stepRef = this.$refs[`step${this.currentStep}`];
      if (!stepRef) return;
      if (!stepRef.validateForm()) return;
      let formPayload = stepRef.getFormPayload();
      this.currentStep = this.config.steps.submitForm;
      this.$nextTick(() => {
        this.$refs.submitStep.submitForm(formPayload);
      });
    },
    reviseData() {
      this.currentStep = this.config.steps.contactForm;
      this.isLastStep = true;
      this.updateButtons();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.contact-wizard {
  margin-top: 20px;
  width: 100%;
}
.step-container {
  min-height: 360px;
}

.step-container > div {
  display: none;
}

.step-container > div.visible {
  display: flex;
  flex-direction: column;
}

.button-container {
  margin-top: 20px;
  display: flex;
}
</style>
